import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { createConfetti, isMajorPrize } from '../utils/confetti';

// Mapping des types de lots vers les images correspondantes
const PRIZE_IMAGES = {
  'passion': '/images/passion.jpg',
  'force': '/images/force.jpg',
  'complicite': '/images/complicité.jpg',
  'tendresse': '/images/tendresse.jpg',
  'amitie': '/images/amitié.jpg',
  'aventure': '/images/aventure.jpg',
  'try_again_1': null,
  'try_again_2': null
};

const Result = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [playHistory, setPlayHistory] = useState(null); // Historique de tous les tours
  const [totalPlays, setTotalPlays] = useState(1);

  const loadResult = useCallback(async () => {
    try {
      const response = await api.get(`/game/result/${token}`);
      if (!response.data.played) {
        setError('Cette participation n\'a pas encore ete jouee. Redirection vers le jeu...');
        setTimeout(() => navigate(`/play/${token}`), 2000);
        return;
      }

      // Récupérer l'historique depuis le backend
      setResult(response.data);
      if (response.data.plays_history && response.data.plays_history.length > 0) {
        setPlayHistory(response.data.plays_history);
      }
      if (response.data.total_plays) {
        setTotalPlays(response.data.total_plays);
      }
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement du resultat');
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    loadResult();
  }, [loadResult]);

  // Déclencher les confettis quand le résultat est chargé et que c'est un gagnant
  useEffect(() => {
    const hasWonPrize = playHistory
      ? playHistory.some(p => p.prize_code)
      : (result && result.is_winner);

    if (hasWonPrize) {
      // Vérifier si c'est un gros lot
      const major = isMajorPrize(result?.prize_type);
      // Délai court pour laisser l'animation de chargement se terminer
      setTimeout(() => {
        createConfetti(major ? 300 : 150, major);
      }, 300);
    }
  }, [result, playHistory]);

  const isWinner = result?.is_winner;
  const bgGradient = isWinner 
    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    : 'linear-gradient(135deg, #ff9966 0%, #ff5e62 100%)';

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" role="status" style={{ color: '#FFD700' }}>
            <span className="visually-hidden">Chargement...</span>
          </div>
          <h3 style={{ color: '#FFD700' }}>Chargement du resultat...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="card text-center p-5 shadow-lg" style={{ maxWidth: '500px', borderRadius: '15px', background: 'rgba(26, 26, 46, 0.9)', border: '2px solid #FFD700' }}>
          <span className="fa-solid fa-circle-exclamation fa-4x mb-3" style={{ color: '#FFD700' }}></span>
          <h3 style={{ color: '#FFD700' }}>Attention</h3>
          <p className="text-light">{error}</p>
        </div>
      </div>
    );
  }

  // Override background with REAKTOR dark theme
  const reaktorBgGradient = isWinner
    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)';

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center py-4 py-md-5" style={{ background: reaktorBgGradient }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="card border-0 shadow-lg" style={{ borderRadius: '25px', overflow: 'hidden', background: 'rgba(26, 26, 46, 0.95)', backdropFilter: 'blur(10px)', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
              <div className="card-body p-4 p-md-5 text-center">
                {/* Afficher le nombre de tours si plusieurs tours joués */}
                {playHistory && playHistory.length > 1 && (
                  <div className="mb-3">
                    <span className="badge px-4 py-2" style={{ background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)', color: '#fff', fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
                      <span className="fa-solid fa-gamepad me-2"></span>
                      {playHistory.length} tours joués
                    </span>
                  </div>
                )}

                {isWinner || (playHistory && playHistory.some(p => p.prize_code)) ? (
                  <>
                    {/* REAKTOR Trophy Logo */}
                    <div className="mb-4">
                      <div className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-lg mb-3"
                        style={{
                          width: 'clamp(80px, 20vw, 120px)',
                          height: 'clamp(80px, 20vw, 120px)',
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          boxShadow: '0 0 30px rgba(255, 215, 0, 0.5)'
                        }}>
                        <span className="fa-solid fa-trophy" style={{ fontSize: 'clamp(30px, 10vw, 60px)', color: '#1a1a2e' }}></span>
                      </div>
                      <h1 className="fw-bold mb-2" style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', color: '#FFD700' }}>FELICITATIONS !</h1>
                      <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', color: '#FFA500' }}>
                        {playHistory && playHistory.length > 1
                          ? `Vous avez gagné ${playHistory.filter(p => p.prize_code).length} lot${playHistory.filter(p => p.prize_code).length > 1 ? 's' : ''} !`
                          : 'Vous avez gagné un lot reaktor !'}
                      </p>
                    </div>

                    <div className="my-4 p-3 p-md-4 rounded-4" style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' }}>
                      {result?.prize_type && PRIZE_IMAGES[result.prize_type] && (
                        <div className="mb-3">
                          <img
                            src={PRIZE_IMAGES[result.prize_type]}
                            alt={result?.prize_label}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '250px',
                              width: 'auto',
                              height: 'auto',
                              borderRadius: '12px',
                              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none' }} className="text-center p-3">
                            <span className="fa-solid fa-gift fa-3x" style={{ color: 'rgba(26, 26, 46, 0.3)' }}></span>
                          </div>
                        </div>
                      )}
                      <h3 className="mb-2" style={{ fontSize: 'clamp(1.3rem, 3vw, 1.8rem)', color: '#1a1a2e' }}>
                        <span className="fa-solid fa-gift me-2"></span>
                        {result?.prize_label}
                      </h3>
                      {result?.prize_description && (
                        <p className="mb-0" style={{ color: 'rgba(26, 26, 46, 0.8)', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>{result.prize_description}</p>
                      )}
                    </div>

                    {result?.prize_code && (
                      <div className="my-4">
                        <div className="p-3 p-md-4 rounded-4" style={{ background: 'rgba(255, 215, 0, 0.15)', border: '2px solid #FFD700' }}>
                          <h5 className="mb-3" style={{ color: '#FFD700' }}>
                            <span className="fa-solid fa-ticket-alt me-2"></span>Votre Code Gagnant
                          </h5>
                          <div className="fw-bold my-3" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontFamily: 'monospace', letterSpacing: '3px', color: '#FFD700' }}>
                            {result.prize_code}
                          </div>
                          <hr style={{ borderColor: 'rgba(255, 215, 0, 0.3)' }} />
                          <small className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                            Presentez ce code à l'hotesse du point de vente pour recupérer votre lot.
                          </small>
                        </div>
                      </div>
                    )}

                    <div className="d-flex justify-content-center gap-2 gap-md-3 mt-4 flex-wrap">
                      <button
                        onClick={() => navigate('/hostess')}
                        className="btn px-3 px-md-4 py-2"
                        style={{
                          background: 'rgba(255, 215, 0, 0.2)',
                          border: '2px solid #FFD700',
                          color: '#FFD700',
                          borderRadius: '50px',
                          fontWeight: 'bold'
                        }}>
                        <span className="fa-solid fa-arrow-left me-2"></span>Retour hôtesse
                      </button>
                      <button
                        onClick={() => window.print()}
                        className="btn px-3 px-md-4 py-2"
                        style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          border: 'none',
                          color: '#1a1a2e',
                          borderRadius: '50px',
                          fontWeight: 'bold'
                        }}>
                        <span className="fa-solid fa-print me-2"></span>Imprimer
                      </button>
                    </div>

                    {/* Afficher tous les lots gagnés si plusieurs tours */}
                    {playHistory && playHistory.length > 1 && (
                      <div className="mt-4">
                        <hr style={{ borderColor: 'rgba(255, 215, 0, 0.3)' }} />
                        <h5 className="mb-3" style={{ color: '#FFD700' }}>
                          <span className="fa-solid fa-list me-2"></span>
                          Tous vos lots ({playHistory.filter(p => p.prize_code).length})
                        </h5>
                        <div className="row g-2">
                          {playHistory.map((play, idx) => (
                            <div className="col-6 col-md-4" key={idx}>
                              <div className="p-2 rounded-3 text-center h-100" style={{
                                background: play.prize_code
                                  ? 'linear-gradient(135deg, rgba(82, 196, 26, 0.2) 0%, rgba(82, 196, 26, 0.1) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 165, 0, 0.2) 0%, rgba(255, 165, 0, 0.1) 100%)',
                                border: `1px solid ${play.prize_code ? '#52c41a' : '#ffa940'}`
                              }}>
                                <div className="mb-1" style={{
                                  fontSize: 'clamp(0.75rem, 1.3vw, 0.85rem)',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  fontWeight: 600
                                }}>
                                  Tour {play.play_number || play.playNumber}
                                </div>
                                {play.prize_code ? (
                                  <>
                                    <div className="mb-1" style={{
                                      fontSize: 'clamp(0.85rem, 1.6vw, 1rem)',
                                      color: '#52c41a',
                                      fontWeight: 700
                                    }}>
                                      {play.prize_label || 'Gagné !'}
                                    </div>
                                    <div style={{
                                      fontSize: 'clamp(0.65rem, 1.1vw, 0.75rem)',
                                      color: '#FFD700',
                                      fontFamily: 'monospace',
                                      fontWeight: 700
                                    }}>
                                      {play.prize_code}
                                    </div>
                                  </>
                                ) : (
                                  <div style={{
                                    fontSize: 'clamp(0.85rem, 1.6vw, 1rem)',
                                    color: '#ffa940'
                                  }}>
                                    Réessayez
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <div className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-lg mb-3"
                        style={{
                          width: 'clamp(80px, 20vw, 120px)',
                          height: 'clamp(80px, 20vw, 120px)',
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          boxShadow: '0 0 30px rgba(255, 215, 0, 0.5)'
                        }}>
                        <span className="fa-solid fa-bolt" style={{ fontSize: 'clamp(30px, 10vw, 60px)', color: '#1a1a2e' }}></span>
                      </div>
                      <h1 className="fw-bold mb-2" style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', color: '#FFD700' }}>Dommage !</h1>
                      <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', color: 'rgba(255, 255, 255, 0.7)' }}>Vous n'avez pas gagne cette fois-ci.</p>
                    </div>

                    <div className="my-4 p-4 rounded-4" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                      <h4 className="mb-3" style={{ color: '#FFD700' }}>
                        <span className="fa-solid fa-lightbulb me-2"></span>
                        Ne perdez pas espoir !
                      </h4>
                      <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>
                        Achetez 2 nouvelles bouteilles Reaktor et tentez à nouveau votre chance.<br />
                        La Saint-Valentin approche !
                      </p>
                    </div>

                    <div className="d-flex justify-content-center gap-3 mt-4">
                      <button
                        onClick={() => navigate('/hostess')}
                        className="btn px-4 py-2"
                        style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          border: 'none',
                          color: '#1a1a2e',
                          borderRadius: '50px',
                          fontWeight: 'bold'
                        }}>
                        <span className="fa-solid fa-arrow-left me-2"></span>Retour hôtesse
                      </button>
                    </div>
                  </>
                )}

                <div className="mt-4 mt-md-5 pt-4" style={{ borderTop: '1px solid rgba(255, 215, 0, 0.2)' }}>
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-2"
                      style={{
                        width: '40px',
                        height: '40px',
                        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                      }}>
                      <span className="fa-solid fa-bolt" style={{ color: '#1a1a2e', fontSize: '18px' }}></span>
                    </div>
                    <span className="fw-bold me-2" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: '#FFD700' }}>REAKTOR</span>
                    <span className="badge" style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1a1a2e' }}>
                      Saint-Valentin 2026
                    </span>
                  </div>
                  <p className="small mb-0" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Campagne promotionnelle "La Roue des Emotions"
                  </p>
                  <small style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Joué le: {result?.played_at ? new Date(result.played_at).toLocaleString('fr-FR') : '-'}
                  </small>
                </div>
              </div>
            </div>

            <div className="text-center mt-4" style={{ color: '#FFD700' }}>
              <p className="mb-2" style={{ fontSize: 'clamp(0.95rem, 2vw, 1.1rem)' }}>
                <span className="fa-solid fa-info-circle me-2"></span>
                Regles du jeu
              </p>
              <small style={{ color: 'rgba(255, 215, 0, 0.75)', fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)' }}>
                <div>• 1 participation par jour et par personne</div>
                <div>• Achat de 2 bouteilles minimum</div>
                <div>• Presentation obligatoire du ticket de caisse</div>
                <div>• Les lots sont a recuperer sur le point de vente</div>
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Result;
